﻿import { EngineRegistry } from '../DrawingEngine.js';
import { DrawingEngine2020 } from '../DrawingEngine2020.js';
import { HtmlDrawingService } from './HtmlDrawingService.js';
import { HtmlAspectRatioSizer, HtmlSizerStrategy } from './HtmlAspectRatioSizer.js';

/**
 * The HtmlDrawingEngine is responsible for creating product drawings using html and css.
 */
export class HtmlDrawingEngine extends DrawingEngine2020 {
  constructor(name, options) {
    super(name, options);
    this.hoverCursor = 'pointer';
    this.sizer = new HtmlAspectRatioSizer(HtmlSizerStrategy.pctWidthPxHeight);
    //this.usesStageProduct = true;
  }

  createDrawingService() {
    return new HtmlDrawingService(this.productDrawing, this.loader.loadedMaterials, this.getFinishName,
      // Give the service a call back to use if it needs to load a single material just-in-time:
      async (material, loadHighResAsWell, onHisResImgLoaded) => {
        return await this.loadSingleMaterial(material, loadHighResAsWell, onHisResImgLoaded);
      });
  }

  /**
   * The owner class (e.g. ProductDrawing) can call this method to get the display object to be
   * associated with the stage product. The stage product is a code created root product instance
   * and the parent of the product being displayed/edited.
   */
  getStageDisplayObject() {
    // We don't need a stage display object
    return null;
  }

  /** Needed if the user want's to move the html element into a full screen display. */
  getRootHtmlElement(rootDisplayObject) {
    /* Canvas-based engines need to return the Canvas element (the rootDisplayObject is a mesh or
     * other proprietary object), but in this html based engine the rootDisplayObject is also the
     * root html element. */
    return rootDisplayObject;
  }

  /**
   * Is responsible for adding a display object to the drawing. Typically this only needs to happen
   * for the root display object, and with some engine this is already done by the fact that the
   * display objects were added to the drawing when they were instantiated (Pixi, Babylon). These
   * subclasses may therefore implement this method with a do-nothing statement.
   * @param {any} displayObject The html element, mesh or sprite representing a (root) product component
   */
  addToDrawing(displayObject) {
    if (displayObject) {
      this.productDrawing.appendChild(displayObject);
    }
  }

  /**
   * Returns an object with the width and height of the drawing. This should match the size of the
   * image returned by the getDrawingImageUrl method.
   */
  getDrawingSize() {
    throw Error('getDrawingSize has not been implemented yet');
  }

  getDrawingImageUrl() {
    throw Error('getDrawingImageUrl has not been implemented yet');
  }

  /**
   * Turns interaction on/off for the displayObject.
   * @param {displayObject} displayObject The object whose interaction is toggled.
   * @param {bool} on Whether to turn interaction on or off.
   * @param {callback} clickHandler Handler for the displayObject's click event.
   */
  toggleDisplayObjectInteractive(displayObject, on, clickHandler) {
    if (displayObject) {
      displayObject.style.cursor = on ? this.hoverCursor : '';
      if (on) {
        displayObject.addEventListener('click', clickHandler);
      } else {
        displayObject.removeEventListener('click', clickHandler);
      }
      displayObject.interactive = on; // Our custom attribute
    }
  }

  startRendering() {
    if (!this.isRendering()) {
      // The HtmlDrawingEngine doesn't have a need for a render loop. It's done automatically by the browser.
      this._isRendering = true;
      return true;
    }
    return false;
  }

  stopRendering() {
    if (this.isRendering()) {
      // The HtmlDrawingEngine doesn't have a need for a render loop. It's done automatically by the browser.
      this._isRendering = false;
      return true;
    }
    return false;
  }

  isRendering() {
    return this._isRendering;
  }

  renderOnce() {
    /* The HtmlDrawingEngine doesn't have a need for manually rendering the drawing. It's done
     * automatically by the browser. */
  }

  //#region Canvas Methods

  bestFitStageAndCanvas(fitCanvasAroundStage, renderOnce) {
    const displayObject = this.productDrawing.firstChild;
    if (displayObject && displayObject.dataComponent) {
      const rect = displayObject.dataComponent.rect;
      let width;
      let height;
      const rotation = displayObject.dataComponent.rotation;
      if (rotation.x === -90) {
        // E.g. a flexi shelf
        width = rect.width;
        height = rect.depth;
      } else if (rotation.y === 90) {
        // E.g. a flexi side
        width = rect.depth;
        height = rect.height;
      } else {
        width = rect.width;
        height = rect.height;
      }
      this.sizer.maximizeWithinParent(displayObject, width, height);
      if (fitCanvasAroundStage) {
        this.resizeCanvasToFitAroundStage(false);
      }
    }
  }

  // As of 8/17/2020 not in use, but should still be functional
  resizeCanvasToFitInFullScreen(renderOnce) {
    throw Error('resizeCanvasToFitInFullScreen must be implemented by the subclass');
  }

  resizeCanvasToFitInFullWindow(renderOnce) {
    // TODO: Implement
    if (renderOnce) {
      this.renderOnce();
    }
  }

  resizeCanvasToFitAroundStage(renderOnce) {
    // TODO: Implement if needed
    if (renderOnce) {
      this.renderOnce();
    }
  }

  //#endregion Canvas Methods
}

EngineRegistry.register('HtmlDrawingEngine', HtmlDrawingEngine);
